import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Linkedin } from "lucide-react";
import { isRTL, getLocalizedPath } from "@/i18n";

const socials = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
];

export default function Footer() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const rtl = isRTL(i18n.language);

  const lang = i18n.language;

  const footerColumns = [
    {
      title: t("footer.quick_links"),
      links: [
        { label: t("footer.links.sale"), href: `/${lang}#listings` },
        { label: t("footer.links.rent"), href: `/${lang}#listings` },
        { label: t("footer.links.land"), href: `/${lang}#listings` },
        { label: t("footer.links.consulting"), to: `/${lang}/invest` },
      ],
    },
    {
      title: t("footer.regions"),
      links: [
        { label: t("hero.search.regions.girne"), href: `/${lang}#listings` },
        { label: t("hero.search.regions.lefkosa"), href: `/${lang}#listings` },
        { label: t("hero.search.regions.gazimagusa"), href: `/${lang}#listings` },
        { label: t("hero.search.regions.iskele"), href: `/${lang}#listings` },
      ],
    },
    {
      title: t("footer.corporate"),
      links: [
        { label: t("footer.links.about"), to: getLocalizedPath("about", lang) },
        { label: t("footer.links.references"), to: getLocalizedPath("about", lang) },
        { label: t("header.investor_relations"), to: `/${lang}/investor-relations` },
        { label: t("footer.links.privacy"), href: "#" },
        { label: t("footer.links.terms"), href: "#" },
      ],
    },
  ];

  return (
    <footer id="contact" data-testid="footer-section" className="bg-white border-t border-gray-200" dir={rtl ? "rtl" : "ltr"}>
      {/* Main Footer */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <span className="text-lg font-semibold text-gray-900 tracking-tight">
              OlyInvestment
            </span>
            <p className="mt-4 text-sm text-gray-600 leading-relaxed max-w-sm">
              {t("footer.brand_desc")}
            </p>
            <div className="mt-6 flex gap-4">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="text-gray-400 hover:text-gray-900 transition-colors"
                    aria-label={s.label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link Columns */}
          {footerColumns.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link
                        to={link.to}
                        className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Column */}
          <div>
            <h4 className="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">
              {t("header.contact")}
            </h4>
            <ul className="space-y-2.5 text-sm text-gray-500">
              <li>Girne, KKTC</li>
              <li>+90 533 800 00 00</li>
              <li>info@olyinvestment.com</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Newsletter Strip */}
      <div className="border-t border-gray-200">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            {t("footer.newsletter_title")}
          </p>
          <div className="flex w-full sm:w-auto max-w-sm">
            <input
              type="email"
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("footer.newsletter_placeholder")}
              className="flex-1 bg-white border border-gray-300 text-gray-700 text-sm px-4 py-2 placeholder:text-gray-400 focus:outline-none focus:border-gray-500"
            />
            <button
              data-testid="newsletter-submit-btn"
              className="px-5 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800 transition-colors"
            >
              {t("footer.newsletter_submit", "Abone Ol")}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} OlyInvestment. {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-6 text-xs text-gray-400">
            <a href="#" className="hover:text-gray-900 transition-colors">{t("footer.links.privacy")}</a>
            <a href="#" className="hover:text-gray-900 transition-colors">{t("footer.links.terms")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
